import {
  DuctType,
  RegType,
  type Flow,
  type FullAirHandlerUnit,
  type UpdateAhuFlow,
  type UpdateAirHandlerUnitRequestBody,
} from "@eisox/api/core-api";
import { z } from "@eisox/zod";

import {
  MAXIMUM_CO2_SETPOINT,
  MAXIMUM_FLOW_SETPOINT,
  MAXIMUM_HUMIDITY_SETPOINT,
  MAXIMUM_PRESSURE_SETPOINT,
  MAXIMUM_SUMMER_INOCC_TEMPERATURE_SETPOINT,
  MAXIMUM_SUMMER_OCC_TEMPERATURE_SETPOINT,
  MAXIMUM_WINTER_INOCC_TEMPERATURE_SETPOINT,
  MAXIMUM_WINTER_OCC_TEMPERATURE_SETPOINT,
  MINIMUM_CO2_SETPOINT,
  MINIMUM_FLOW_SETPOINT,
  MINIMUM_HUMIDITY_SETPOINT,
  MINIMUM_PRESSURE_SETPOINT,
  MINIMUM_SUMMER_INOCC_TEMPERATURE_SETPOINT,
  MINIMUM_SUMMER_OCC_TEMPERATURE_SETPOINT,
  MINIMUM_WINTER_INOCC_TEMPERATURE_SETPOINT,
  MINIMUM_WINTER_OCC_TEMPERATURE_SETPOINT,
} from "~/constants/air-handler-unit.constant";
import { m } from "~/paraglide/messages";

import { getKeyLabel } from "./data-selector.helper";

const SUMMER = "summer";
const WINTER = "winter";
const OCCUPIED = "occupied";
const UNOCCUPIED = "unoccupied";

type Season = typeof SUMMER | typeof WINTER;
type Period = typeof OCCUPIED | typeof UNOCCUPIED;

export const SETPOINT_SEASONS = [SUMMER, WINTER] as const satisfies readonly Season[];

/**
 * The unqualified temperature setpoints of a CTA are its heating ones. The cooling setpoint of a
 * couple must stay strictly above its heating setpoint: the dead band between the heating and the
 * cooling coil.
 */
export const TEMPERATURE_SETPOINT_COUPLES = [
  {
    season: SUMMER,
    period: OCCUPIED,
    heating: "summerOccTemperatureSetpoint",
    cooling: "summerOccCoolingTemperatureSetpoint",
    min: MINIMUM_SUMMER_OCC_TEMPERATURE_SETPOINT,
    max: MAXIMUM_SUMMER_OCC_TEMPERATURE_SETPOINT,
  },
  {
    season: SUMMER,
    period: UNOCCUPIED,
    heating: "summerInoccTemperatureSetpoint",
    cooling: "summerInoccCoolingTemperatureSetpoint",
    min: MINIMUM_SUMMER_INOCC_TEMPERATURE_SETPOINT,
    max: MAXIMUM_SUMMER_INOCC_TEMPERATURE_SETPOINT,
  },
  {
    season: WINTER,
    period: OCCUPIED,
    heating: "winterOccTemperatureSetpoint",
    cooling: "winterOccCoolingTemperatureSetpoint",
    min: MINIMUM_WINTER_OCC_TEMPERATURE_SETPOINT,
    max: MAXIMUM_WINTER_OCC_TEMPERATURE_SETPOINT,
  },
  {
    season: WINTER,
    period: UNOCCUPIED,
    heating: "winterInoccTemperatureSetpoint",
    cooling: "winterInoccCoolingTemperatureSetpoint",
    min: MINIMUM_WINTER_INOCC_TEMPERATURE_SETPOINT,
    max: MAXIMUM_WINTER_INOCC_TEMPERATURE_SETPOINT,
  },
] as const;

export const HUMIDITY_SETPOINTS = [
  { season: SUMMER, period: OCCUPIED, key: "summerOccHumiditySetpoint" },
  { season: SUMMER, period: UNOCCUPIED, key: "summerInoccHumiditySetpoint" },
  { season: WINTER, period: OCCUPIED, key: "winterOccHumiditySetpoint" },
  { season: WINTER, period: UNOCCUPIED, key: "winterInoccHumiditySetpoint" },
] as const;

export const FLOW_SETPOINTS = [
  { season: SUMMER, period: OCCUPIED, key: "summerOccFlowSetpoint" },
  { season: SUMMER, period: UNOCCUPIED, key: "summerInoccFlowSetpoint" },
  { season: WINTER, period: OCCUPIED, key: "winterOccFlowSetpoint" },
  { season: WINTER, period: UNOCCUPIED, key: "winterInoccFlowSetpoint" },
] as const;

export const PRESSURE_SETPOINTS = [
  { season: SUMMER, period: OCCUPIED, key: "summerOccPressureSetpoint" },
  { season: SUMMER, period: UNOCCUPIED, key: "summerInoccPressureSetpoint" },
  { season: WINTER, period: OCCUPIED, key: "winterOccPressureSetpoint" },
  { season: WINTER, period: UNOCCUPIED, key: "winterInoccPressureSetpoint" },
] as const;

export const CO2_SETPOINTS = [
  { season: SUMMER, period: OCCUPIED, key: "summerOccCO2Setpoint" },
  { season: SUMMER, period: UNOCCUPIED, key: "summerInoccCO2Setpoint" },
  { season: WINTER, period: OCCUPIED, key: "winterOccCO2Setpoint" },
  { season: WINTER, period: UNOCCUPIED, key: "winterInoccCO2Setpoint" },
] as const;

export const HUMIDITY_SETPOINT_KEYS = HUMIDITY_SETPOINTS.map(({ key }) => key);

export const CO2_SETPOINT_KEYS = CO2_SETPOINTS.map(({ key }) => key);

export const SETPOINT_QUANTITIES = ["pressure", "humidity", "flow", "co2"] as const;

export const SETPOINT_SECTIONS = ["general", "temperature", ...SETPOINT_QUANTITIES] as const;

/** Only the supply and the return duct regulate: the two others declare the points and leave them empty. */
export const SETPOINT_DUCTS = [DuctType.SUPPLY, DuctType.RETURN] as const;

export const PRESSURE_SETPOINT_KEYS = PRESSURE_SETPOINTS.map(({ key }) => key);

export const FLOW_SETPOINT_KEYS = FLOW_SETPOINTS.map(({ key }) => key);

type CoolingSetpointKey = (typeof TEMPERATURE_SETPOINT_COUPLES)[number]["cooling"];
type HeatingSetpointKey = (typeof TEMPERATURE_SETPOINT_COUPLES)[number]["heating"];
type HumiditySetpointKey = (typeof HUMIDITY_SETPOINT_KEYS)[number];
type CO2SetpointKey = (typeof CO2_SETPOINT_KEYS)[number];
type SetpointDuct = (typeof SETPOINT_DUCTS)[number];
type PressureSetpointKey = (typeof PRESSURE_SETPOINT_KEYS)[number];
type FlowSetpointKey = (typeof FLOW_SETPOINT_KEYS)[number];
type SetpointQuantity = (typeof SETPOINT_QUANTITIES)[number];
type SetpointSection = (typeof SETPOINT_SECTIONS)[number];
type PressureFieldName = `${SetpointDuct}__${PressureSetpointKey}`;
type FlowFieldName = `${SetpointDuct}__${FlowSetpointKey}`;

/** A setpoint slider reads `<point name> (<unit>)`, the point naming itself as it does in the history. */
export const getSetpointLabel = (point: string, unit: string): string => `${getKeyLabel(point)} (${unit})`;

/**
 * `CSG_SOUF_MIN` / `CSG_SOUF_MAX` are physical limits of the supply duct, so they bound the heating
 * and the cooling setpoint alike -- the automate exposes no cooling-specific pair, and adding one
 * would describe the same duct twice.
 *
 * They only constrain a configured setpoint when the CTA regulates on the supply air. Under an
 * ambient or a return regulation the setpoint is a room temperature, which the duct limits say
 * nothing about, and a CTA reporting a narrow duct range would otherwise forbid perfectly valid
 * room setpoints.
 */
export const getTemperatureBounds = (
  airHandlerUnit: FullAirHandlerUnit | undefined,
  bounds: { min: number; max: number },
): { min: number; max: number } => {
  if (airHandlerUnit?.regType !== RegType.SUPPLY_SETPOINT) return bounds;

  return {
    min: airHandlerUnit.supplyAirMinTemperatureSetpoint ?? bounds.min,
    max: airHandlerUnit.supplyAirMaxTemperatureSetpoint ?? bounds.max,
  };
};

/** A bound the automate reports outranks the hard-coded range, side by side. */
const withReportedBounds = (
  bounds: { min: number; max: number },
  reported: { min?: number; max?: number },
): { min: number; max: number } => ({ min: reported.min ?? bounds.min, max: reported.max ?? bounds.max });

export const getPressureBounds = (
  airHandlerUnit: FullAirHandlerUnit | undefined,
  bounds: { min: number; max: number },
): { min: number; max: number } =>
  withReportedBounds(bounds, {
    min: airHandlerUnit?.pressureMinSetpoint,
    max: airHandlerUnit?.pressureMaxSetpoint,
  });

export const getFlowBounds = (
  airHandlerUnit: FullAirHandlerUnit | undefined,
  bounds: { min: number; max: number },
): { min: number; max: number } =>
  withReportedBounds(bounds, { min: airHandlerUnit?.flowMinSetpoint, max: airHandlerUnit?.flowMaxSetpoint });

export const getHumidityBounds = (
  airHandlerUnit: FullAirHandlerUnit | undefined,
  bounds: { min: number; max: number },
): { min: number; max: number } =>
  withReportedBounds(bounds, {
    min: airHandlerUnit?.humidityMinSetpoint,
    max: airHandlerUnit?.humidityMaxSetpoint,
  });

export const getCO2Bounds = (
  airHandlerUnit: FullAirHandlerUnit | undefined,
  bounds: { min: number; max: number },
): { min: number; max: number } =>
  withReportedBounds(bounds, { min: airHandlerUnit?.co2MinSetpoint, max: airHandlerUnit?.co2MaxSetpoint });

/** A setpoint the CTA does not report is unconstrained: only a complete couple carries the rule. */
export const isCoolingSetpointValid = (heating?: number, cooling?: number): boolean =>
  heating === undefined || cooling === undefined || cooling > heating;

/**
 * The dead band is a typing convenience: the API only knows the heating and the cooling setpoint,
 * which sit symmetrically around the entered setpoint.
 */
export const toHeatingSetpoint = (setpoint: number, deadBand: number): number => setpoint - deadBand / 2;

export const toCoolingSetpoint = (setpoint: number, deadBand: number): number => setpoint + deadBand / 2;

export const toSetpointAndDeadBand = (heating: number, cooling: number): { setpoint: number; deadBand: number } => ({
  setpoint: (heating + cooling) / 2,
  deadBand: cooling - heating,
});

export const getCoolingSetpointDefaultValues = (
  airHandlerUnit: FullAirHandlerUnit | undefined,
): Record<CoolingSetpointKey, number | undefined> =>
  Object.fromEntries(TEMPERATURE_SETPOINT_COUPLES.map(({ cooling }) => [cooling, airHandlerUnit?.[cooling]])) as Record<
    CoolingSetpointKey,
    number | undefined
  >;

/** Only a couple the CTA reports both sides of can be entered as a setpoint and a dead band. */
export const isCompleteTemperatureCouple = (
  airHandlerUnit: FullAirHandlerUnit,
  { heating, cooling }: { heating: HeatingSetpointKey; cooling: CoolingSetpointKey },
): boolean => airHandlerUnit[heating] !== undefined && airHandlerUnit[cooling] !== undefined;

export const getHumiditySetpointDefaultValues = (
  airHandlerUnit: FullAirHandlerUnit | undefined,
): Record<HumiditySetpointKey, number | undefined> =>
  Object.fromEntries(HUMIDITY_SETPOINT_KEYS.map(key => [key, airHandlerUnit?.[key]])) as Record<
    HumiditySetpointKey,
    number | undefined
  >;

export const getCO2SetpointDefaultValues = (
  airHandlerUnit: FullAirHandlerUnit | undefined,
): Record<CO2SetpointKey, number | undefined> =>
  Object.fromEntries(CO2_SETPOINT_KEYS.map(key => [key, airHandlerUnit?.[key]])) as Record<
    CO2SetpointKey,
    number | undefined
  >;

const ductFieldName = <K extends string>(type: SetpointDuct, key: K): `${SetpointDuct}__${K}` => `${type}__${key}`;

export const pressureFieldName = (type: SetpointDuct, key: PressureSetpointKey): PressureFieldName =>
  ductFieldName(type, key);

export const flowFieldName = (type: SetpointDuct, key: FlowSetpointKey): FlowFieldName => ductFieldName(type, key);

const DUCT_KEYS = [...PRESSURE_SETPOINT_KEYS, ...FLOW_SETPOINT_KEYS];

const DUCT_FIELD_NAMES = new Set<string>(
  SETPOINT_DUCTS.flatMap(type => DUCT_KEYS.map(key => ductFieldName(type, key))),
);

const findFlow = (flows: Flow[] | undefined, type: SetpointDuct): Flow | undefined =>
  flows?.find(candidate => candidate.type === type);

export const getPressureSetpointDefaultValues = (
  flows: Flow[] | undefined,
): Record<PressureFieldName, number | undefined> =>
  Object.fromEntries(
    SETPOINT_DUCTS.flatMap(type => {
      const flow = findFlow(flows, type);
      return PRESSURE_SETPOINT_KEYS.map(key => [pressureFieldName(type, key), flow?.[key]]);
    }),
  ) as Record<PressureFieldName, number | undefined>;

export const hasPressureSetpoints = (flows: Flow[] | undefined): boolean =>
  SETPOINT_DUCTS.some(type => {
    const flow = findFlow(flows, type);
    return PRESSURE_SETPOINT_KEYS.some(key => flow?.[key] !== undefined);
  });

export const getFlowSetpointDefaultValues = (flows: Flow[] | undefined): Record<FlowFieldName, number | undefined> =>
  Object.fromEntries(
    SETPOINT_DUCTS.flatMap(type => {
      const flow = findFlow(flows, type);
      return FLOW_SETPOINT_KEYS.map(key => [flowFieldName(type, key), flow?.[key]]);
    }),
  ) as Record<FlowFieldName, number | undefined>;

/** A bound the CTA leaves out is unconstrained: only a complete pair carries the rule. */
export const isSetpointBoundValid = (min?: number, max?: number): boolean =>
  min === undefined || max === undefined || min < max;

/**
 * Holds a slider one step short of the other side of its pair, as the mid-season sliders do: the
 * thumb stops instead of crossing, so the form never has to report a couple out of order.
 */
export const holdBelow = (value: number | undefined, other: number | undefined, step: number) =>
  value === undefined || other === undefined ? value : Math.min(value, other - step);

export const holdAbove = (value: number | undefined, other: number | undefined, step: number) =>
  value === undefined || other === undefined ? value : Math.max(value, other + step);

/**
 * A setpoint left outside the bounds -- by a bound the installer is tightening, or by one the
 * automate reports narrower than the value it reports -- would sit past the end of its slider.
 */
export const widenBoundsTo = (
  bounds: { min: number; max: number },
  value: number | undefined,
): { min: number; max: number } =>
  value === undefined ? bounds : { min: Math.min(bounds.min, value), max: Math.max(bounds.max, value) };

export const hasFlowSetpoints = (flows: Flow[] | undefined): boolean =>
  SETPOINT_DUCTS.some(type => {
    const flow = findFlow(flows, type);
    return FLOW_SETPOINT_KEYS.some(key => flow?.[key] !== undefined);
  });

const toFlowsPatch = (dirtyValues: Record<string, unknown>, flows: Flow[] | undefined): UpdateAhuFlow[] | undefined => {
  const patches = SETPOINT_DUCTS.flatMap(type => {
    const flow = findFlow(flows, type);
    if (!flow) return [];

    const dirtyPoints = DUCT_KEYS.filter(key => ductFieldName(type, key) in dirtyValues).map(
      key => [key, dirtyValues[ductFieldName(type, key)]] as const,
    );

    return dirtyPoints.length > 0 ? [{ id: flow.id, ...Object.fromEntries(dirtyPoints) }] : [];
  });

  return patches.length > 0 ? patches : undefined;
};

/**
 * Duct setpoints are flattened into `<duct>__<setpoint>` form fields so `getDirtyValues` can
 * diff them, and are folded back into the nested `flows[]` the endpoint expects. The flow ids
 * belong to the patched CTA, so a group builds one body per unit.
 */
export const toSetpointPatch = (
  dirtyValues: Record<string, unknown>,
  flows: Flow[] | undefined,
): UpdateAirHandlerUnitRequestBody => {
  const body = Object.fromEntries(Object.entries(dirtyValues).filter(([key]) => !DUCT_FIELD_NAMES.has(key)));
  const flowsPatch = toFlowsPatch(dirtyValues, flows);

  return { ...body, ...(flowsPatch && { flows: flowsPatch }) };
};

/** Every min / max pair the form guards. A bound describes the machine, so the CTA carries them all. */
export const SETPOINT_BOUND_COUPLES = [
  { section: "temperature", min: "supplyAirMinTemperatureSetpoint", max: "supplyAirMaxTemperatureSetpoint" },
  { section: "humidity", min: "humidityMinSetpoint", max: "humidityMaxSetpoint" },
  { section: "pressure", min: "pressureMinSetpoint", max: "pressureMaxSetpoint" },
  { section: "flow", min: "flowMinSetpoint", max: "flowMaxSetpoint" },
  { section: "co2", min: "co2MinSetpoint", max: "co2MaxSetpoint" },
] as const;

type BoundFieldName = (typeof SETPOINT_BOUND_COUPLES)[number]["min" | "max"];

/** The values of the setpoint form: every setting the page offers, duct setpoints flattened. */
export const getSetpointFormValues = (airHandlerUnit: FullAirHandlerUnit | undefined) => {
  const humidityDefaults = getHumidityBounds(airHandlerUnit, {
    min: MINIMUM_HUMIDITY_SETPOINT,
    max: MAXIMUM_HUMIDITY_SETPOINT,
  });
  const pressureDefaults = getPressureBounds(airHandlerUnit, {
    min: MINIMUM_PRESSURE_SETPOINT,
    max: MAXIMUM_PRESSURE_SETPOINT,
  });
  const flowDefaults = getFlowBounds(airHandlerUnit, { min: MINIMUM_FLOW_SETPOINT, max: MAXIMUM_FLOW_SETPOINT });
  const co2Defaults = getCO2Bounds(airHandlerUnit, { min: MINIMUM_CO2_SETPOINT, max: MAXIMUM_CO2_SETPOINT });

  return {
    minimumOutsideAirDampersSetpoint: airHandlerUnit?.minimumOutsideAirDampersSetpoint,
    antiFreezeAlarmSetpoint: airHandlerUnit?.antiFreezeAlarmSetpoint,
    supplyAirMinTemperatureSetpoint: airHandlerUnit?.supplyAirMinTemperatureSetpoint,
    supplyAirMaxTemperatureSetpoint: airHandlerUnit?.supplyAirMaxTemperatureSetpoint,
    humidityMinSetpoint: humidityDefaults.min,
    humidityMaxSetpoint: humidityDefaults.max,
    pressureMinSetpoint: pressureDefaults.min,
    pressureMaxSetpoint: pressureDefaults.max,
    flowMinSetpoint: flowDefaults.min,
    flowMaxSetpoint: flowDefaults.max,
    co2MinSetpoint: co2Defaults.min,
    co2MaxSetpoint: co2Defaults.max,
    summerOccTemperatureSetpoint: airHandlerUnit?.summerOccTemperatureSetpoint,
    summerInoccTemperatureSetpoint: airHandlerUnit?.summerInoccTemperatureSetpoint,
    winterOccTemperatureSetpoint: airHandlerUnit?.winterOccTemperatureSetpoint,
    winterInoccTemperatureSetpoint: airHandlerUnit?.winterInoccTemperatureSetpoint,
    ...getCoolingSetpointDefaultValues(airHandlerUnit),
    ...getHumiditySetpointDefaultValues(airHandlerUnit),
    ...getCO2SetpointDefaultValues(airHandlerUnit),
    ...getPressureSetpointDefaultValues(airHandlerUnit?.flows),
    ...getFlowSetpointDefaultValues(airHandlerUnit?.flows),
  };
};

type SetpointFormValues = ReturnType<typeof getSetpointFormValues>;

export const setpointFormOptions = { defaultValues: {} as SetpointFormValues };

export const setpointFormSchema = z.custom<SetpointFormValues>().superRefine((values, context) => {
  for (const { heating, cooling } of TEMPERATURE_SETPOINT_COUPLES) {
    if (isCoolingSetpointValid(values[heating], values[cooling])) continue;
    context.addIssue({ code: "custom", path: [cooling], message: m.air_handler_unit_cooling_setpoint_above_heating() });
  }
  for (const { min, max } of SETPOINT_BOUND_COUPLES) {
    if (isSetpointBoundValid(values[min], values[max])) continue;
    context.addIssue({ code: "custom", path: [max], message: m.air_handler_unit_setpoint_bound_min_below_max() });
  }
});

const ERROR_FIELD_SECTIONS = new Map<string, SetpointSection>([
  ...TEMPERATURE_SETPOINT_COUPLES.map(({ cooling }) => [cooling, "temperature"] as const),
  ...SETPOINT_BOUND_COUPLES.map(({ max, section }) => [max, section] as const),
]);

/** The sections showing the errors of the schema, so a refused save opens where they sit. */
export const getSetpointErrorSections = (fieldNames: readonly string[]): SetpointSection[] =>
  fieldNames.flatMap(name => ERROR_FIELD_SECTIONS.get(name) ?? []);

export const SETPOINT_PERIOD_TITLE: Record<Period, () => string> = {
  occupied: m.occupation,
  unoccupied: m.inoccupation,
};

/**
 * The heating / cooling couples the CTA reports a side of. The duct limits the automate reports
 * outrank the hard-coded range, for the cooling setpoint as much as for the heating one.
 */
export const getTemperatureCouples = (airHandlerUnit: FullAirHandlerUnit) =>
  TEMPERATURE_SETPOINT_COUPLES.filter(
    ({ heating, cooling }) => airHandlerUnit[heating] !== undefined || airHandlerUnit[cooling] !== undefined,
  ).map(couple => ({ ...couple, ...getTemperatureBounds(airHandlerUnit, couple) }));

type TemperatureCouple = ReturnType<typeof getTemperatureCouples>[number];

/** The section a setpoint link points at opens; one the CTA does not display falls back to the first. */
export const getOpenedSetpointSection = (
  displayedSections: readonly SetpointSection[],
  requestedSection?: SetpointSection,
): SetpointSection | undefined =>
  requestedSection && displayedSections.includes(requestedSection) ? requestedSection : displayedSections[0];

export type {
  BoundFieldName,
  CO2SetpointKey,
  CoolingSetpointKey,
  FlowFieldName,
  FlowSetpointKey,
  HeatingSetpointKey,
  HumiditySetpointKey,
  Period,
  PressureFieldName,
  PressureSetpointKey,
  Season,
  SetpointDuct,
  SetpointFormValues,
  SetpointQuantity,
  SetpointSection,
  TemperatureCouple,
};
